// Portfolio Stress Test & Scenario Simulator.
//
// Mounted at /dashboard/portfolio-stress-test. The page:
//   1. Reads the user's persisted risk-assessment results.
//      * If the result is missing OR has no ``portfolio.allocation``,
//        we render an explainer + CTA that bounces the user into the
//        risk-assessment questionnaire. Without an allocation there
//        is nothing meaningful to stress-test.
//   2. Loads the scenario catalogue from
//      GET /portfolio-stress-test/scenarios.
//   3. Lets the user pick a scenario, set an investment amount and a
//      time horizon (both as range sliders, mirroring the request).
//   4. Calls POST /portfolio-stress-test/stress-test on every change
//      (debounced) and renders the Before / After / Projected bar
//      chart + a per-asset breakdown table.
//
// Visual design intentionally mirrors the Risk Assessment results
// dashboard (surface-card, page-title / subtitle, slate/emerald
// palette) so the two screens feel like the same product.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { riskApi } from '../risk-assessment/api/riskApi';
import type { PersistedAssessmentResult } from '../risk-assessment/api/riskApi';
import StressTestChart from './components/StressTestChart';
import { SensitivityChart } from './components/SensitivityChart'; // <-- NEW
import {
    stressTestApi,
    type ScenarioCatalogue,
    type StressTestResponse,
} from './api/stressTestApi';

// ---------------------------------------------------------------------------
// Inline icons -- kept dependency-free like the rest of the design system.
// ---------------------------------------------------------------------------
const ChartIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-7 w-7"
        aria-hidden
    >
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 3 3 6-7" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden
    >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

const InfoIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden
    >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
    </svg>
);

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------
type BootState =
    | { kind: 'loading' }
    | { kind: 'no-allocation' } // user needs to complete the assessment
    | {
          kind: 'ready';
          allocation: Record<string, number>;
          riskProfile: string | null;
          scenarios: ScenarioCatalogue;
      }
    | { kind: 'error'; message: string };

// Range inputs constants -- kept at the module level so the slider
// labels and the request validation stay in sync.
//
// Investment amount uses a PIECEWISE scale instead of a single linear
// step so the granularity tracks the user's likely sensitivity:
// retail savers care about the gap between $3k and $5k; a $4.99M vs
// $5.00M difference is noise. Native <input type="range"> only
// supports a single linear step, so the slider's ``value`` is a
// position index (0..N-1) into ``INVESTMENT_STOPS`` and the parent
// translates back to dollars on each change.
//
// Stops:
//   $1k -> $4k    in $1k  steps  (4  stops)
//   $5k -> $95k   in $5k  steps  (19 stops)
//   $100k -> $950k in $50k steps  (18 stops)
//   $1M -> $5M    in $1M  steps  (5  stops)
//   = 46 stops total. Default $100k sits at index 23.
const INVESTMENT_STOPS: readonly number[] = (() => {
    const stops: number[] = [];
    for (let v = 1_000; v < 5_000; v += 1_000) stops.push(v);
    for (let v = 5_000; v < 100_000; v += 5_000) stops.push(v);
    for (let v = 100_000; v < 1_000_000; v += 50_000) stops.push(v);
    for (let v = 1_000_000; v <= 5_000_000; v += 1_000_000) stops.push(v);
    return stops;
})();
const INVESTMENT_MIN = INVESTMENT_STOPS[0];
const INVESTMENT_DEFAULT = 100_000;

/**
 * Map a dollar amount to the index of the closest stop on the
 * piecewise investment scale. Used to:
 *   * derive the slider's initial position from
 *     ``INVESTMENT_DEFAULT`` (or any persisted previous value),
 *   * snap arbitrary numeric input to a renderable stop.
 *
 * Linear scan is fine -- the array is 46 entries -- and avoids
 * allocating an O(log N) binary-search helper for a single use.
 */
function investmentToIndex(amount: number): number {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < INVESTMENT_STOPS.length; i++) {
        const d = Math.abs(INVESTMENT_STOPS[i] - amount);
        if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
        }
    }
    return bestIdx;
}

const HORIZON_MIN = 1;
const HORIZON_MAX = 50;  // <-- Changed from 30 to 50
const HORIZON_DEFAULT = 5;

// Debounce window for /stress-test calls -- short enough that the
// chart feels live, long enough that we don't fire on every pixel of
// slider drag.
const STRESS_TEST_DEBOUNCE_MS = 250;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatMoney(value: number): string {
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}

function formatPct(value: number, digits = 2): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(digits)}%`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const NoAllocationPanel = () => (
    <section className="surface-card max-w-2xl">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">
            <InfoIcon />
        </div>
        <h2 className="section-title">Take the Risk Assessment first</h2>
        <p className="mt-3 text-sm muted">
            The Portfolio Stress Test runs scenarios against your{' '}
            <strong className="text-slate-900 dark:text-white">
                recommended asset allocation
            </strong>
            . That allocation is produced by the Risk Intelligence Suite from
            your answers to the 43-question profiler. Once you've completed
            the questionnaire we'll plug your allocation straight into this
            simulator — no data entry required.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
            <Link
                to="/dashboard/risk-assessment/test"
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-slate-200 px-4 py-2.5 text-sm font-bold text-white dark:text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
                Take the Risk Assessment <ArrowRightIcon />
            </Link>
            <Link
                to="/dashboard/risk-assessment"
                className="inline-flex items-center justify-center rounded-xl border-2 border-slate-300 dark:border-slate-700 px-4 py-2.5 text-sm font-bold text-slate-900 dark:text-white transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
                Back to Risk Intelligence Suite
            </Link>
        </div>
    </section>
);

interface ScenarioDropdownProps {
    scenarios: string[];
    selected: string;
    onChange: (next: string) => void;
}

const ScenarioDropdown = ({
    scenarios,
    selected,
    onChange,
}: ScenarioDropdownProps) => {
    return (
        <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide muted">
                Scenario
            </span>
            <div className="relative mt-2">
                <select
                    value={selected}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full appearance-none rounded-xl border-2 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/60 px-4 py-3 pr-10 text-sm font-semibold text-slate-900 dark:text-white shadow-sm transition-colors hover:border-emerald-400 dark:hover:border-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                >
                    {scenarios.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500"
                    aria-hidden
                >
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </div>
        </label>
    );
};

interface RangeFieldProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    format: (v: number) => string;
    onChange: (next: number) => void;
}

const RangeField = ({
    label,
    value,
    min,
    max,
    step = 1,
    format,
    onChange,
}: RangeFieldProps) => {
    // Compute the filled-track width for the gradient indicator.
    const pct = ((value - min) / (max - min)) * 100;

    return (
        <label className="block">
            <div className="flex items-baseline justify-between">
                <span className="text-xs font-bold uppercase tracking-wide muted">
                    {label}
                </span>
                <span className="text-base font-black tabular-nums text-slate-900 dark:text-white">
                    {format(value)}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="mt-2 w-full appearance-none h-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                style={{
                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${pct}%, rgba(148,163,184,0.35) ${pct}%, rgba(148,163,184,0.35) 100%)`,
                }}
            />
            <div className="mt-1 flex justify-between text-xs muted tabular-nums">
                <span>{format(min)}</span>
                <span>{format(max)}</span>
            </div>
        </label>
    );
};

interface ResultSummaryProps {
    result: StressTestResponse;
}

const ResultSummary = ({ result }: ResultSummaryProps) => {
    const { metrics } = result;

    // --- Card A Logic (Overall Scenario Shock) ---
    // Colour rule: positive -> GREEN, negative -> RED, exactly 0.00 -> ORANGE.
    const isOverallLoss = result.portfolio_change_pct < 0;
    const isOverallGain = result.portfolio_change_pct > 0;
    const accentA = isOverallLoss
        ? 'text-red-600 dark:text-red-400'
        : isOverallGain
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-amber-600 dark:text-amber-400';
    const bgAccentA = isOverallLoss
        ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
        : isOverallGain
        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900'
        : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900';

    // --- Card B Logic (Impact at first shock) ---
    const hasShock = metrics.first_shock_year !== null;
    let shockAbs = 0;
    let shockPct = 0;

    if (hasShock && metrics.first_shock_before_value !== null && metrics.first_shock_after_value !== null) {
        shockAbs = metrics.first_shock_after_value - metrics.first_shock_before_value;
        shockPct = metrics.first_shock_before_value !== 0 ? (shockAbs / metrics.first_shock_before_value) * 100 : 0;
    }

    const isShockLoss = shockAbs < 0;
    const isShockGain = shockAbs > 0;
    // Colour rule: positive -> GREEN, negative -> RED, exactly 0.00 -> ORANGE.
    const accentB = isShockLoss
        ? 'text-red-600 dark:text-red-400'
        : isShockGain
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-amber-600 dark:text-amber-400';
    const bgAccentB = !hasShock
        ? 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800'
        : isShockLoss
        ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
        : isShockGain
        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900'
        : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900';

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* CARD A: Overall Scenario Shock */}
            <div
                className={`rounded-xl border-2 px-4 py-3 flex flex-col justify-between ${bgAccentA}`}
                title="One-time impact applied in each selected shock year, computed as the allocation-weighted average of the scenario's per-asset returns."
            >
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide muted">
                        Scenario shock (weighted)
                    </p>
                    <p className="mt-1 text-[11px] leading-snug muted">
                        One-time impact applied in each selected shock year, computed as the allocation-weighted average of the scenario's per-asset returns.
                    </p>
                    <p className={`mt-1 text-2xl font-black tabular-nums ${accentA}`}>
                        {formatPct(result.portfolio_change_pct)}
                    </p>
                </div>
            </div>

            {/* CARD B: Context-aware Impact at First Shock */}
            <div className={`rounded-xl border-2 px-4 py-3 flex flex-col justify-between ${bgAccentB}`}>
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide muted">
                        {hasShock ? `Impact at first shock (Year ${metrics.first_shock_year})` : 'Impact at first shock'}
                    </p>
                    {hasShock ? (
                        <>
                            <p className={`mt-1 text-2xl font-black tabular-nums ${accentB}`}>
                                {shockAbs > 0 ? '+' : ''}{formatMoney(shockAbs)}
                            </p>
                            <p className="mt-0.5 text-xs font-medium muted flex items-center gap-1.5 flex-wrap">
                                <span>{formatPct(shockPct)}</span>
                                {isShockLoss && metrics.break_even_year !== null && (
                                    <>
                                        <span className="opacity-50">•</span>
                                        <span className="text-slate-700 dark:text-slate-300">
                                            Breaks even Yr {metrics.break_even_year}
                                        </span>
                                    </>
                                )}
                            </p>
                        </>
                    ) : (
                        <p className="mt-1 text-base font-medium text-slate-500 dark:text-slate-400">
                            No shock selected
                        </p>
                    )}
                </div>
            </div>

            {/* CARD C: Projected & Net Gains */}
            <div className="rounded-xl border-2 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 px-4 py-3 flex flex-col justify-between">
                <div>
                    <div className="flex justify-between items-start">
                        <p className="text-xs font-bold uppercase tracking-wide muted">
                            Projected at horizon
                        </p>
                        {metrics.estimated_liquidation_tax > 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200/50 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                Pre-Tax
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-white" title="Pre-tax gross portfolio value at the end of the time horizon.">
                        {formatMoney(result.projected_value)}
                    </p>
                    <p className="mt-0.5 text-xs muted">
                        {result.time_horizon_years}-yr • {formatPct(result.projected_cagr_pct, 2)} / yr
                    </p>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700/60 text-[11px] font-medium text-slate-600 dark:text-slate-400 space-y-1">
                    <p className="flex justify-between">
                        <span>vs initial:</span>
                        <span className={`tabular-nums ${metrics.net_gain_vs_initial > 0 ? 'text-emerald-600 dark:text-emerald-400' : metrics.net_gain_vs_initial < 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {metrics.net_gain_vs_initial > 0 ? '+' : ''}{formatMoney(metrics.net_gain_vs_initial)}
                        </span>
                    </p>
                    <p className="flex justify-between pb-1">
                        <span title="Net gain versus total out-of-pocket cash (Initial + All Contributions).">vs invested:</span>
                        <span className={`tabular-nums ${metrics.net_gain_vs_invested > 0 ? 'text-emerald-600 dark:text-emerald-400' : metrics.net_gain_vs_invested < 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {metrics.net_gain_vs_invested > 0 ? '+' : ''}{formatMoney(metrics.net_gain_vs_invested)}
                        </span>
                    </p>

                    {/* NEW: Final Liquidation Math */}
                    {metrics.estimated_liquidation_tax > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700/60 space-y-1 animate-fade-in">
                            <p className="flex justify-between text-slate-500" title={`Calculated using an adjusted cost basis of ${formatMoney(metrics.liquidation_cost_basis)} to prevent double taxation on reinvested dividends.`}>
                                <span>Est. liquidation tax:</span>
                                <span className="text-red-500 dark:text-red-400 tabular-nums">
                                    -{formatMoney(metrics.estimated_liquidation_tax)}
                                </span>
                            </p>
                            <p className="flex justify-between font-bold text-slate-800 dark:text-slate-200">
                                <span>Net cash if sold:</span>
                                <span className="text-emerald-600 dark:text-emerald-400 tabular-nums text-[12px]">
                                    {formatMoney(metrics.projected_value_after_tax)}
                                </span>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Map each canonical asset-class label (as returned by the backend in
// ``per_asset[].asset``) to its dedicated ranking page. Clicking an asset
// row in the breakdown deep-links the user straight into the corresponding
// asset-class leaderboard, tightening the loop between the stress-test view
// and the per-asset intelligence pages. "Cash & Savings Accounts" has no
// ranking page, so it is intentionally omitted (stays static).
const ASSET_RANKING_ROUTES: Record<string, string> = {
    'Equities (Stocks)': '/dashboard/stock-ranking',
    'ETFs & Treasuries': '/dashboard/etf-ranking',
    Commodities: '/dashboard/commodities-ranking',
    REITs: '/dashboard/reits-ranking',
    Cryptocurrencies: '/dashboard/crypto-ranking',
    'Foreign Exchange (FX & Global Exposure)': '/dashboard/fx-ranking',
};

interface PerAssetTableProps {
    rows: StressTestResponse['per_asset'];
}

const PerAssetTable = ({ rows }: PerAssetTableProps) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-left">
                    <th className="py-2 pr-4 text-xs font-bold uppercase tracking-wide muted">
                        Asset class
                    </th>
                    <th className="py-2 px-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Weight
                    </th>
                    <th className="py-2 px-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Shock
                    </th>
                    <th className="py-2 px-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Before
                    </th>
                    <th className="py-2 px-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        After
                    </th>
                    <th className="py-2 pl-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Δ
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => {
                    const isLoss = row.absolute_change < 0;
                    const isGain = row.absolute_change > 0;
                    // Colour rule: positive -> GREEN, negative -> RED, exactly 0.00 -> ORANGE.
                    const accent = isLoss
                        ? 'text-red-600 dark:text-red-400'
                        : isGain
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-amber-600 dark:text-amber-400';
                    const rankingRoute = ASSET_RANKING_ROUTES[row.asset];
                    return (
                        <tr
                            key={row.asset}
                            className="border-b border-slate-100 dark:border-slate-800/60 last:border-b-0"
                        >
                            <td className="py-2 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                {rankingRoute ? (
                                    <Link
                                        to={rankingRoute}
                                        title={`View ${row.asset} ranking`}
                                        className="group inline-flex items-center gap-1.5 text-slate-900 hover:text-emerald-600 dark:text-slate-100 dark:hover:text-emerald-400 transition-colors"
                                    >
                                        <span className="underline decoration-dotted decoration-slate-300 dark:decoration-slate-600 underline-offset-4 group-hover:decoration-emerald-500">
                                            {row.asset}
                                        </span>
                                        <svg
                                            aria-hidden
                                            className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-emerald-500 transition-colors"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </Link>
                                ) : (
                                    row.asset
                                )}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums muted">
                                {row.weight_pct.toFixed(1)}%
                            </td>
                            <td
                                className={`py-2 px-2 text-right tabular-nums font-semibold ${accent}`}
                            >
                                {formatPct(row.asset_return_pct, 1)}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums">
                                {formatMoney(row.before_value)}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums">
                                {formatMoney(row.after_value)}
                            </td>
                            <td
                                className={`py-2 pl-2 text-right tabular-nums font-bold ${accent}`}
                            >
                                {row.absolute_change >= 0 ? '+' : '-'}
                                {formatMoney(Math.abs(row.absolute_change))}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const PortfolioStressTestPage = () => {
    const [boot, setBoot] = useState<BootState>({ kind: 'loading' });

    // User inputs.
    const [scenario, setScenario] = useState<string>('');
    const [investment, setInvestment] = useState<number>(INVESTMENT_DEFAULT);
    const [horizon, setHorizon] = useState<number>(HORIZON_DEFAULT);

    // --- NEW: Advanced Simulation Config ---
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [advancedConfig, setAdvancedConfig] = useState({
        contribution_amount: 0,
        contribution_periodicity: 'monthly' as const,
        is_dividend_distributing: false,
        dividend_yield_pct: 0,           // <-- CHANGED
        dividend_periodicity: 'annually' as const,
        capital_gains_tax_pct: 0,
        fee_pct_annual: 0,               // <-- CHANGED
    });

    // We use a raw string for the user to type comma-separated years
    // (e.g., "0, 3, 5") and parse it just before the API call.
    const [scenarioYearsStr, setScenarioYearsStr] = useState<string>('0');

    // Live result.
    const [result, setResult] = useState<StressTestResponse | null>(null);
    const [resultError, setResultError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);

    // Boot: load risk-assessment + scenarios in parallel.
    useEffect(() => {
        let cancelled = false;
        Promise.all([riskApi.getLatestResults(), stressTestApi.getScenarios()])
            .then(([persisted, scenarios]) => {
                if (cancelled) return;
                const allocation = extractAllocation(persisted);
                if (!allocation) {
                    setBoot({ kind: 'no-allocation' });
                    return;
                }
                const scenarioNames = Object.keys(scenarios.scenarios);
                setBoot({
                    kind: 'ready',
                    allocation,
                    riskProfile: persisted?.risk_profile ?? null,
                    scenarios: scenarios.scenarios,
                });
                if (scenarioNames.length > 0 && !scenario) {
                    setScenario(scenarioNames[0]);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('[PortfolioStressTestPage] boot failed', err);
                setBoot({
                    kind: 'error',
                    message:
                        'We could not load the stress-test simulator. ' +
                        'Refresh the page or come back in a moment.',
                });
            });
        return () => {
            cancelled = true;
        };
        // We deliberately omit ``scenario`` -- this effect is the
        // boot path and must run exactly once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced /stress-test runner.
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const runStressTest = useCallback(
        (
            allocation: Record<string, number>,
            scenarioName: string,
            amount: number,
            years: number,
            config: typeof advancedConfig, // <--- NEW
            shockYearsStr: string, // <--- NEW
        ) => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(async () => {
                setRunning(true);
                setResultError(null);
                try {
                    // Parse "0, 3, 5" into [0, 3, 5], capping safely at max years
                    const parsedYears = shockYearsStr
                        .split(',')
                        .map((s) => parseInt(s.trim(), 10))
                        .filter((n) => !isNaN(n) && n >= 0 && n <= years);

                    const safeShockYears =
                        parsedYears.length > 0 ? parsedYears : [0];

                    const data = await stressTestApi.runStressTest({
                        allocation,
                        scenario: scenarioName,
                        investment_amount: amount,
                        time_horizon_years: years,
                        ...config, // <--- Spread the advanced settings
                        scenario_occurrence_years: safeShockYears,
                    });
                    setResult(data);
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[PortfolioStressTestPage] run failed', err);
                    setResultError(
                        'The simulator could not compute that combination. ' +
                            'Try a different scenario or refresh.',
                    );
                } finally {
                    setRunning(false);
                }
            }, STRESS_TEST_DEBOUNCE_MS);
        },
        [],
    );

    // Re-run whenever any input (or the boot allocation) changes.
    useEffect(() => {
        if (boot.kind !== 'ready') return;
        if (!scenario) return;
        runStressTest(
            boot.allocation,
            scenario,
            investment,
            horizon,
            advancedConfig,
            scenarioYearsStr,
        );
    }, [
        boot,
        scenario,
        investment,
        horizon,
        advancedConfig,
        scenarioYearsStr,
        runStressTest,
    ]);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (boot.kind === 'loading') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Portfolio Stress Test"
                subtitle="Loading your recommended allocation…"
            >
                <div className="surface-card-skeleton">
                    <div className="skeleton-line-lg w-1/3 mb-4" />
                    <div className="skeleton-line w-full mb-2" />
                    <div className="skeleton-line w-2/3" />
                </div>
            </Layout>
        );
    }

    if (boot.kind === 'error') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Portfolio Stress Test"
                subtitle="Something went wrong."
            >
                <p className="surface-card-error">{boot.message}</p>
                <Link
                    to="/dashboard/risk-assessment"
                    className="mt-4 inline-block text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                    ← Back to Risk Intelligence Suite
                </Link>
            </Layout>
        );
    }

    if (boot.kind === 'no-allocation') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Portfolio Stress Test & Scenario Simulator"
                subtitle="See how your recommended allocation behaves under real-world market shocks."
            >
                <NoAllocationPanel />
            </Layout>
        );
    }

    const scenarioNames = Object.keys(boot.scenarios);

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Portfolio Stress Test & Scenario Simulator"
            subtitle="See how your recommended allocation behaves under real-world market shocks."
        >
            {/* Breadcrumb back to the suite portal. */}
            <div className="mb-4">
                <Link
                    to="/dashboard/risk-assessment"
                    className="text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400"
                >
                    ← Risk Intelligence Suite
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ------ Left: inputs ----------------------------- */}
                <section className="lg:col-span-1 surface-card flex flex-col gap-5">
                    <div>
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <ChartIcon />
                        </div>
                        <h2 className="section-title">Scenario inputs</h2>
                        <p className="mt-2 text-sm muted">
                            Pick a market shock, set how much you've invested
                            and how long you'll stay invested. The chart
                            updates as you move the sliders.
                        </p>
                    </div>

                    <ScenarioDropdown
                        scenarios={scenarioNames}
                        selected={scenario}
                        onChange={setScenario}
                    />

                    {/* Slider operates on a position INDEX into the
                        ``INVESTMENT_STOPS`` table (see top of file)
                        rather than a raw dollar amount, so the user
                        can step through small amounts ($1k..$5k by
                        $1k, $5k..$100k by $5k, etc.) without dragging
                        through millions of irrelevant cents at the
                        low end. The format callback still receives
                        the index, so we look up the dollar value to
                        show the user. ``INVESTMENT_MIN`` /
                        ``INVESTMENT_MAX`` shown on the legend rail
                        come from the stops endpoints directly. */}
                    <RangeField
                        label="Investment amount"
                        value={investmentToIndex(investment)}
                        min={0}
                        max={INVESTMENT_STOPS.length - 1}
                        step={1}
                        format={(idx) =>
                            formatMoney(
                                INVESTMENT_STOPS[idx] ?? INVESTMENT_MIN,
                            )
                        }
                        onChange={(idx) =>
                            setInvestment(
                                INVESTMENT_STOPS[idx] ?? INVESTMENT_DEFAULT,
                            )
                        }
                    />

                    <RangeField
                        label="Time horizon (years)"
                        value={horizon}
                        min={HORIZON_MIN}
                        max={HORIZON_MAX}
                        step={1}
                        format={(v) => `${v} yr${v === 1 ? '' : 's'}`}
                        onChange={setHorizon}
                    />

                    {/* --- ADVANCED SETTINGS ACCORDION --- */}
                    <div className="mt-2 border-t border-slate-200 dark:border-slate-800 pt-4">
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="flex w-full items-center justify-between text-sm font-bold text-slate-700 dark:text-slate-300 hover:text-emerald-600 transition-colors focus:outline-none"
                        >
                            <span>Advanced Cash Flows &amp; Timing</span>
                            <svg
                                className={`h-4 w-4 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>

                        {showAdvanced && (
                            <div className="mt-4 space-y-5 animate-fade-slide-up rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                                {/* 1. Contributions */}
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="text-xs font-semibold muted block mb-1">
                                            Contributions ($)
                                        </span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="100"
                                            value={advancedConfig.contribution_amount || ''}
                                            onChange={(e) =>
                                                setAdvancedConfig((prev) => ({
                                                    ...prev,
                                                    contribution_amount:
                                                        Number(e.target.value) || 0,
                                                }))
                                            }
                                            className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                            placeholder="0"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-xs font-semibold muted block mb-1">
                                            Frequency
                                        </span>
                                        <select
                                            value={advancedConfig.contribution_periodicity}
                                            onChange={(e) =>
                                                setAdvancedConfig((prev) => ({
                                                    ...prev,
                                                    contribution_periodicity: e.target
                                                        .value as typeof prev.contribution_periodicity,
                                                }))
                                            }
                                            className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                        >
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="semi-annually">
                                                Semi-Annually
                                            </option>
                                            <option value="annually">Annually</option>
                                        </select>
                                    </label>
                                </div>

                                {/* 2. Fees & Taxes */}
                                <div className="grid grid-cols-2 gap-3 border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <label className="block">
                                        <span className="text-xs font-semibold muted block mb-1">
                                            Annual AUM Fee (%)
                                        </span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="0.05"
                                            value={advancedConfig.fee_pct_annual || ''}
                                            onChange={(e) =>
                                                setAdvancedConfig((prev) => ({
                                                    ...prev,
                                                    fee_pct_annual:
                                                        Number(e.target.value) || 0,
                                                }))
                                            }
                                            className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                            placeholder="e.g. 1.0"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-xs font-semibold muted block mb-1">
                                            Cap Gains Tax (%)
                                        </span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={advancedConfig.capital_gains_tax_pct || ''}
                                            onChange={(e) =>
                                                setAdvancedConfig((prev) => ({
                                                    ...prev,
                                                    capital_gains_tax_pct:
                                                        Number(e.target.value) || 0,
                                                }))
                                            }
                                            className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                            placeholder="0"
                                        />
                                    </label>
                                </div>

                                {/* 3. Dividends & Taxes */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <label className="block">
                                            <span className="text-xs font-semibold muted block mb-1">
                                                Annual Div. Yield (%)
                                            </span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                                value={advancedConfig.dividend_yield_pct || ''}
                                                onChange={(e) =>
                                                    setAdvancedConfig((prev) => ({
                                                        ...prev,
                                                        dividend_yield_pct:
                                                            Number(e.target.value) || 0,
                                                    }))
                                                }
                                                className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                                placeholder="e.g. 2.0"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="text-xs font-semibold muted block mb-1">
                                                Payout Frequency
                                            </span>
                                            <select
                                                value={advancedConfig.dividend_periodicity}
                                                onChange={(e) =>
                                                    setAdvancedConfig((prev) => ({
                                                        ...prev,
                                                        dividend_periodicity: e.target
                                                            .value as typeof prev.dividend_periodicity,
                                                    }))
                                                }
                                                className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                            >
                                                <option value="quarterly">Quarterly</option>
                                                <option value="semi-annually">Semi-Annually</option>
                                                <option value="annually">Annually</option>
                                            </select>
                                        </label>
                                    </div>
                                    
                                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 hover:border-emerald-400 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={advancedConfig.is_dividend_distributing}
                                            onChange={(e) =>
                                                setAdvancedConfig((prev) => ({
                                                    ...prev,
                                                    is_dividend_distributing:
                                                        e.target.checked,
                                                }))
                                            }
                                            className="mt-0.5 w-4 h-4 text-emerald-500 rounded focus:ring-emerald-400 border-slate-300 dark:border-slate-700"
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                                Distribute Dividends as Cash
                                            </span>
                                            <span className="text-[10px] muted mt-1 leading-relaxed">
                                                {advancedConfig.is_dividend_distributing
                                                    ? "Dividends leave the compounding pool. Overall portfolio growth will be heavily reduced."
                                                    : "Dividends are reinvested automatically. You still pay taxes on them (Tax Drag)."}
                                            </span>
                                        </div>
                                    </label>
                                </div>

                                {/* 4. Scenario Timing */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <label className="block">
                                        <span className="text-xs font-semibold muted block mb-1">
                                            Years to apply shock (comma separated)
                                        </span>
                                        <input
                                            type="text"
                                            placeholder="e.g. 0, 3, 5"
                                            value={scenarioYearsStr}
                                            onChange={(e) =>
                                                setScenarioYearsStr(e.target.value)
                                            }
                                            className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
                                        />
                                        <p className="text-[10px] muted mt-1">
                                            Year 0 applies the scenario immediately.
                                        </p>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* --- END ADVANCED SETTINGS ACCORDION --- */}

                    <div className="mt-2 rounded-xl bg-slate-100 dark:bg-slate-800/60 p-3 text-xs muted">
                        Profile in use:{' '}
                        <span className="font-bold text-slate-900 dark:text-white">
                            {boot.riskProfile ?? 'Latest recommendation'}
                        </span>
                        {' · '}allocation pulled from your latest risk-
                        assessment results.
                    </div>
                </section>

                {/* ------ Right: results --------------------------- */}
                <section className="lg:col-span-2 surface-card">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div>
                            <h2 className="section-title">{scenario}</h2>
                            <p className="text-sm muted mt-1">
                                Single-shock impact, then recovery at the
                                portfolio's weighted long-run expected return.
                            </p>
                        </div>
                        {running && (
                            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                Running…
                            </span>
                        )}
                    </div>

                    {resultError && (
                        <p className="surface-card-error mb-4">{resultError}</p>
                    )}

                    {result ? (
                        <div className="space-y-6 animate-fade-slide-up">
                            <ResultSummary result={result} />

                            {/* --- THE NEW TIME-SERIES CHART --- */}
                            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4 pt-6 h-[min(50vh,520px)] flex flex-col w-full">
                                <StressTestChart data={result.chart_data} />
                            </div>

                            {/* --- NEW: Simulation Cash Flow Summary --- */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm">
                                    <p className="text-[10px] font-bold uppercase tracking-wider muted">
                                        Contributions
                                    </p>
                                    <p className="mt-1 text-base font-black tabular-nums text-emerald-600 dark:text-emerald-400">
                                        + {formatMoney(result.metrics.total_contributions)}
                                    </p>
                                </div>
                                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm">
                                    <p className="text-[10px] font-bold uppercase tracking-wider muted">
                                        Net Dividends
                                    </p>
                                    <p className="mt-1 text-base font-black tabular-nums text-emerald-600 dark:text-emerald-400">
                                        + {formatMoney(result.metrics.total_dividends_net)}
                                    </p>
                                </div>
                                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm">
                                    <p className="text-[10px] font-bold uppercase tracking-wider muted">
                                        Fees Paid
                                    </p>
                                    <p className="mt-1 text-base font-black tabular-nums text-red-600 dark:text-red-400">
                                        - {formatMoney(result.metrics.total_fees_paid)}
                                    </p>
                                </div>
                                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm">
                                    <p className="text-[10px] font-bold uppercase tracking-wider muted">
                                        Taxes Withheld
                                    </p>
                                    <p className="mt-1 text-base font-black tabular-nums text-red-600 dark:text-red-400">
                                        - {formatMoney(result.metrics.total_taxes_paid)}
                                    </p>
                                </div>
                            </div>

                            {/* --- NEW: Advanced Drag & Sensitivity Analytics --- */}
                            {(result.metrics.total_fees_paid > 0 || result.metrics.total_taxes_paid > 0) && result.chart_data.length > 1 && (
                                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 animate-fade-slide-up">
                                    <h3 className="section-title mb-4">
                                        Drag & Sensitivity Analysis
                                    </h3>
                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

                                        {/* Fee Sensitivity Chart */}
                                        {result.metrics.total_fees_paid > 0 && (
                                            <div className="flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-4 shadow-sm h-[320px]">
                                                <SensitivityChart
                                                    data={result.chart_data}
                                                    baselineKey="portfolio_value_no_fee"
                                                    actualKey="portfolio_value"
                                                    title="Fee Drag (Lost Compounding)"
                                                    description={`Compares actual trajectory vs a theoretical $0 fee portfolio. Notice how compounding drag heavily outpaces the raw ${formatMoney(result.metrics.total_fees_paid)} actually paid.`}
                                                    idealLabel="No Fees"
                                                    actualLabel="With Fees"
                                                    dragLabel="Lost Wealth"
                                                />
                                            </div>
                                        )}

                                        {/* Tax Sensitivity Chart */}
                                        {result.metrics.total_taxes_paid > 0 && (
                                            <div className="flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-4 shadow-sm h-[320px]">
                                                <SensitivityChart
                                                    data={result.chart_data}
                                                    baselineKey="cumulative_dividends_gross"
                                                    actualKey="cumulative_dividends_net"
                                                    title="Tax Drag on Dividends"
                                                    description={`Visualizes cumulative gross dividends generated vs what you actually net after ${formatMoney(result.metrics.total_taxes_paid)} in distributions are withheld.`}
                                                    idealLabel="Gross Yield"
                                                    actualLabel="Net After Tax"
                                                    dragLabel="Lost to Taxes"
                                                    dragColor="orange"
                                                />
                                            </div>
                                        )}

                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="surface-card-skeleton">
                            <div className="skeleton-line-lg w-1/3 mb-4" />
                            <div className="skeleton-line w-full mb-2" />
                            <div className="skeleton-line w-2/3" />
                        </div>
                    )}
                </section>
            </div>

            {/* Per-asset drill-down ------------------------------------ */}
            {result && (
                <section className="surface-card mt-6 animate-fade-slide-up">
                    <h3 className="section-title mb-2">Per-asset breakdown</h3>
                    <p className="text-sm muted mb-4">
                        How each asset class in your recommended allocation
                        reacts to the {scenario} scenario.
                    </p>
                    <PerAssetTable rows={result.per_asset} />
                </section>
            )}
        </Layout>
    );
};

// ---------------------------------------------------------------------------
// Helpers (kept at the bottom so the page reads top-down).
// ---------------------------------------------------------------------------

/**
 * Pull the recommended allocation out of a persisted assessment row.
 * Returns ``null`` when the row is missing OR has no completed
 * portfolio (e.g. user only auto-saved a partial draft). The caller
 * uses ``null`` to decide whether to render :func:`NoAllocationPanel`.
 */
function extractAllocation(
    persisted: PersistedAssessmentResult | null,
): Record<string, number> | null {
    if (!persisted) return null;
    const allocation = persisted.portfolio?.allocation;
    if (!allocation || Object.keys(allocation).length === 0) return null;
    return allocation;
}

export default PortfolioStressTestPage;
